import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import Loader from "../../components/UI/Loader";
import Alert from "../../components/UI/Alert";
import { getAlumnoDetalleService } from "../../services/seguimiento/seguimiento.service";

// ── Constantes ─────────────────────────────────────────────────────────────

const AREA_LABELS = {
  1: "¿Cómo construyo relaciones con los demás?",
  2: "¿Cómo aprendo y resuelvo problemas?",
  3: "¿Qué me motiva?",
  4: "¿Qué hago mejor que los demás?",
  5: "¿A qué me debo dedicar? Tus mejores tareas",
  6: "¿Cuál es mi rol en el trabajo de grupo?",
  7: "¿Quiénes pueden ayudarme a desarrollar mi potencial?",
};

const AREA_ICONS = {
  1: "mdi:account-group-outline",
  2: "mdi:puzzle-outline",
  3: "mdi:lightbulb-on-outline",
  4: "mdi:trophy-outline",
  5: "mdi:briefcase-check-outline",
  6: "mdi:account-supervisor-outline",
  7: "mdi:handshake-outline",
};

// Igual que en MisFortalezasLayer (vista "principal" del estudiante).
const AREA_COLORS = [
  { bg: "rgba(16,185,129,0.12)", fg: "#10b981" },
  { bg: "rgba(99,102,241,0.12)", fg: "#818cf8" },
  { bg: "rgba(245,158,11,0.12)", fg: "#f59e0b" },
];

const TABS = [
  { id: "resumen", label: "Las 7 fortalezas", icon: "mdi:shield-star" },
  { id: "confirmacion", label: "Confirmación", icon: "mdi:check-decagram-outline" },
  { id: "fortalezas", label: "Fortalezas", icon: "mdi:shield-star-outline" },
  { id: "aplicacion", label: "Aplicación", icon: "mdi:briefcase-check-outline" },
];

// Re-agrupa la lista de bloques (por pregunta/área con talentos dentro) a la
// vista inversa: por talento con los bloques dentro.
// Entrada: [{pregunta, tipo_fortaleza, talentos: [{talento, frases_seleccionadas}]}]
// Salida:  [{talento, grupos: [{pregunta, tipo_fortaleza, frases_seleccionadas}]}]
const agruparPorTalento = (grupos) => {
  const map = new Map();
  grupos.forEach((g) => {
    (g.talentos || []).forEach((t) => {
      if (!t.talento) return;
      if (!map.has(t.talento)) map.set(t.talento, []);
      map.get(t.talento).push({
        pregunta: g.pregunta,
        tipo_fortaleza: g.tipo_fortaleza,
        frases_seleccionadas: t.frases_seleccionadas || [],
      });
    });
  });
  return Array.from(map.entries()).map(([talento, gs]) => ({
    talento,
    grupos: gs,
  }));
};

// ── Sub-componentes ────────────────────────────────────────────────────────

const SectionHeader = ({ icon, title, subtitle, badge, accent = "#6366f1" }) => (
  <div className="d-flex align-items-start gap-3 mb-4">
    <div
      className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
      style={{
        width: 40,
        height: 40,
        background: `${accent}18`,
        color: accent,
      }}
    >
      <Icon icon={icon} width="20" />
    </div>
    <div className="flex-grow-1">
      <div className="d-flex align-items-center gap-2">
        <h5 className="mb-0 fw-bold text-primary-light">{title}</h5>
        {badge != null && (
          <span
            className="badge rounded-pill fw-semibold"
            style={{ background: `${accent}18`, color: accent }}
          >
            {badge}
          </span>
        )}
      </div>
      {subtitle && (
        <p className="mb-0 text-secondary-light small mt-1">{subtitle}</p>
      )}
    </div>
  </div>
);

// Card réplica del diseño de MisFortalezasLayer (vista "principal" del alumno).
const AreaCard = ({ item, cardIndex = 0 }) => {
  const label = AREA_LABELS[item.tipo_fortaleza] || item.pregunta;
  const icon = AREA_ICONS[item.tipo_fortaleza] || "mdi:help-circle-outline";
  const color = AREA_COLORS[cardIndex % AREA_COLORS.length];
  const totalFrases = item.talentos.reduce(
    (sum, t) => sum + (t.frases_seleccionadas?.length || 0),
    0,
  );

  return (
    <motion.div
      className="col-lg-6 col-12 mb-20"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: 0.1 + cardIndex * 0.08,
        duration: 0.4,
        ease: "easeOut",
      }}
    >
      <div className="card radius-12 h-100 cs-talent-card">
        <div className="card-body p-20">
          {/* ── Header del área ── */}
          <div className="cs-talent-header">
            <div
              className="d-flex align-items-start gap-2"
              style={{ flex: 1, minWidth: 0 }}
            >
              <div
                className="cs-question-icon-wrap d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{
                  width: 36,
                  height: 36,
                  background: color.bg,
                  color: color.fg,
                }}
              >
                <Icon icon={icon} width="18" />
              </div>
              <div style={{ minWidth: 0 }}>
                <h5 className="cs-pregunta-title mb-0">{label}</h5>
              </div>
            </div>
            <span className="cs-talent-badge" style={{ flexShrink: 0 }}>
              {totalFrases} {totalFrases === 1 ? "respuesta" : "respuestas"}
            </span>
          </div>

          {/* ── Talentos del área ── */}
          {item.talentos.length === 0 ? (
            <div className="cs-empty-state mt-12">
              <Icon icon="mdi:minus-circle-outline" />
              <span>Sin respuestas en esta área</span>
            </div>
          ) : (
            <div className="d-flex flex-column gap-12 mt-12">
              {item.talentos.map((t, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: 0.15 + cardIndex * 0.08 + i * 0.05,
                    duration: 0.26,
                  }}
                >
                  {/* Chip del talento */}
                  <div className="d-flex align-items-center gap-2 mb-6">
                    <Icon
                      icon="mdi:star"
                      width="16"
                      style={{ color: color.fg, flexShrink: 0 }}
                    />
                    <span
                      className="text-primary-light fw-semibold"
                      style={{ fontSize: "0.85rem" }}
                    >
                      {t.talento}
                    </span>
                  </div>

                  {/* Frases del talento en esta área */}
                  <div className="d-flex flex-column gap-2 ms-24">
                    {t.frases_seleccionadas.map((f, j) => (
                      <div
                        key={j}
                        className="d-flex align-items-start gap-2"
                      >
                        <Icon
                          icon="mdi:check-circle"
                          width="14"
                          className="mt-1 flex-shrink-0"
                          style={{ color: "#10b981" }}
                        />
                        <span
                          className="text-secondary-light"
                          style={{ fontSize: "0.82rem", lineHeight: 1.5 }}
                        >
                          {f.frase}
                        </span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Card que agrupa POR TALENTO: cada talento muestra sus preguntas/áreas con
// sus frases seleccionadas. `subLabel` = "pregunta" (Confirmación) o "área" (Fortalezas).
const TalentoCard = ({ item, cardIndex = 0, subLabel = "pregunta" }) => {
  const color = AREA_COLORS[cardIndex % AREA_COLORS.length];
  const totalFrases = item.grupos.reduce(
    (sum, g) => sum + (g.frases_seleccionadas?.length || 0),
    0,
  );

  return (
    <motion.div
      className="col-lg-6 col-12 mb-20"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: 0.1 + cardIndex * 0.08,
        duration: 0.4,
        ease: "easeOut",
      }}
    >
      <div className="card radius-12 h-100 cs-talent-card">
        <div className="card-body p-20">
          <div className="cs-talent-header">
            <div
              className="d-flex align-items-start gap-2"
              style={{ flex: 1, minWidth: 0 }}
            >
              <div
                className="cs-question-icon-wrap d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{
                  width: 36,
                  height: 36,
                  background: color.bg,
                  color: color.fg,
                }}
              >
                <Icon icon="mdi:star" width="18" />
              </div>
              <div style={{ minWidth: 0 }}>
                <h5 className="cs-pregunta-title mb-0">{item.talento}</h5>
              </div>
            </div>
            <span className="cs-talent-badge" style={{ flexShrink: 0 }}>
              {totalFrases} {totalFrases === 1 ? "respuesta" : "respuestas"}
            </span>
          </div>

          {item.grupos.length === 0 ? (
            <div className="cs-empty-state mt-12">
              <Icon icon="mdi:minus-circle-outline" />
              <span>Sin respuestas en este talento</span>
            </div>
          ) : (
            <div className="d-flex flex-column gap-12 mt-12">
              {item.grupos.map((g, i) => {
                const preguntaLabel =
                  subLabel === "área"
                    ? g.pregunta
                    : AREA_LABELS[g.tipo_fortaleza] || g.pregunta;
                const icon =
                  AREA_ICONS[g.tipo_fortaleza] || "mdi:help-circle-outline";
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: 0.15 + cardIndex * 0.08 + i * 0.05,
                      duration: 0.26,
                    }}
                  >
                    <div className="d-flex align-items-center gap-2 mb-6">
                      <Icon
                        icon={icon}
                        width="16"
                        style={{ color: color.fg, flexShrink: 0 }}
                      />
                      <span
                        className="text-primary-light fw-semibold"
                        style={{ fontSize: "0.85rem" }}
                      >
                        {preguntaLabel}
                      </span>
                    </div>

                    <div className="d-flex flex-column gap-2 ms-24">
                      {g.frases_seleccionadas.length === 0 ? (
                        <span
                          className="text-secondary-light fst-italic"
                          style={{ fontSize: "0.78rem" }}
                        >
                          — Sin selecciones
                        </span>
                      ) : (
                        g.frases_seleccionadas.map((f, j) => (
                          <div
                            key={j}
                            className="d-flex align-items-start gap-2"
                          >
                            <Icon
                              icon="mdi:check-circle"
                              width="14"
                              className="mt-1 flex-shrink-0"
                              style={{ color: "#10b981" }}
                            />
                            <span
                              className="text-secondary-light"
                              style={{ fontSize: "0.82rem", lineHeight: 1.5 }}
                            >
                              {f.frase}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Toggle pequeño para elegir entre las dos vistas de agrupación.
const GroupToggle = ({ value, onChange, options }) => (
  <div
    className="d-inline-flex p-1 rounded-3 mb-3 bg-neutral-100"
    role="tablist"
  >
    {options.map((opt) => {
      const active = value === opt.id;
      return (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className="btn btn-sm rounded-3 d-inline-flex align-items-center gap-2"
          style={{
            padding: "0.4rem 0.9rem",
            border: "none",
            background: active ? "var(--body-bg, #fff)" : "transparent",
            color: active ? "var(--primary-600, #4f46e5)" : "inherit",
            fontWeight: active ? 700 : 500,
            boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
          }}
        >
          <Icon icon={opt.icon} width="16" />
          <span>{opt.label}</span>
        </button>
      );
    })}
  </div>
);

const AplicacionCard = ({ grupo }) => {
  const totalRespuestas = grupo.actividades.reduce(
    (sum, a) => sum + a.respuestas.length,
    0,
  );
  const totalSi = grupo.actividades.reduce(
    (sum, a) => sum + a.respuestas.filter((r) => r.respuesta).length,
    0,
  );

  return (
    <div className="col-lg-6 col-12 mb-3">
      <div className="border rounded-3 overflow-hidden h-100">
        <div className="d-flex align-items-center gap-3 p-3 border-bottom bg-neutral-50">
          <div
            className="d-flex align-items-center justify-content-center rounded-3 bg-neutral-100 text-secondary-light flex-shrink-0"
            style={{ width: 40, height: 40 }}
          >
            <Icon icon="mdi:star-outline" width="20" />
          </div>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <h6 className="mb-0 fw-bold text-primary-light text-truncate">
              {grupo.talento}
            </h6>
            <div className="d-flex align-items-center gap-3 mt-1 small text-secondary-light flex-wrap">
              <span>{totalRespuestas} respuestas</span>
              <span className="text-success">
                <Icon icon="mdi:check" width="14" className="me-1" />
                {totalSi} sí
              </span>
              <span className="text-danger">
                <Icon icon="mdi:close" width="14" className="me-1" />
                {totalRespuestas - totalSi} no
              </span>
            </div>
          </div>
        </div>

        <div className="p-3">
          {grupo.actividades.map((act, j) => (
            <div key={j} className={j === 0 ? "" : "mt-3 pt-3 border-top"}>
              <div className="d-flex align-items-center gap-2 mb-2">
                <Icon
                  icon="mdi:clipboard-list-outline"
                  width="18"
                  className="text-secondary-light flex-shrink-0"
                />
                <span className="fw-semibold text-primary-light small">
                  {act.actividad}
                </span>
              </div>
              <div className="d-flex flex-column gap-2 ms-1">
                {act.respuestas.map((r, k) => (
                  <div
                    key={k}
                    className="d-flex align-items-center gap-2 p-2 rounded-3 border"
                  >
                    <Icon
                      icon={r.respuesta ? "mdi:check-circle" : "mdi:close-circle"}
                      width="18"
                      className={`flex-shrink-0 ${r.respuesta ? "text-success" : "text-danger"}`}
                    />
                    <span className="small flex-grow-1 text-primary-light">
                      {r.frase}
                    </span>
                    <span
                      className={`badge fw-bold flex-shrink-0 ${r.respuesta ? "bg-success" : "bg-danger"}`}
                      style={{ fontSize: "0.65rem", padding: "3px 8px" }}
                    >
                      {r.respuesta ? "SÍ" : "NO"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TalentoList = ({ items, accent }) => {
  if (!items.length) {
    return (
      <div className="text-center py-4 rounded-3 bg-neutral-50">
        <Icon
          icon="mdi:database-off-outline"
          width="26"
          className="text-secondary-light"
        />
        <p className="text-secondary-light small mb-0 mt-1">Sin datos aún</p>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-2">
      {items.map((t, i) => (
        <div
          key={t.id ?? i}
          className="d-flex align-items-center gap-2 px-3 py-2 rounded-3"
          style={{
            background: `${accent}0d`,
            borderLeft: `3px solid ${accent}`,
          }}
        >
          <span className="fw-semibold text-primary-light">{t.nombre}</span>
        </div>
      ))}
    </div>
  );
};

// ── Componente principal ───────────────────────────────────────────────────

const AlumnoDetallePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("resumen");
  const [confirmacionGroup, setConfirmacionGroup] = useState("pregunta");
  const [fortalezasGroup, setFortalezasGroup] = useState("area");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getAlumnoDetalleService(id);
        setData(res);
      } catch (err) {
        const msg =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          "No se pudo cargar el detalle del alumno.";
        setError(msg);
        if (err.response?.status === 403) toast.error(msg);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="card radius-12">
        <div className="card-body p-24">
          <Loader center text="Cargando detalle..." delay={200} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card radius-12">
        <div className="card-body p-24">
          <Alert message={error} variant="error" size="md" />
          <div className="mt-3">
            <button
              className="btn btn-outline-secondary rounded-pill px-3"
              onClick={() => navigate(-1)}
            >
              <Icon icon="mdi:arrow-left" width="16" className="me-1" />
              Regresar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const {
    estudiante,
    talentos_optimos = [],
    talentos_a_mejorar = [],
    mis_fortalezas = [],
    confirmacion = [],
    fortalezas = [],
    aplicacion = [],
  } = data;

  const tabCounts = {
    resumen: mis_fortalezas.length,
    confirmacion: confirmacion.length,
    fortalezas: fortalezas.length,
    aplicacion: aplicacion.length,
  };

  return (
    <div>
      {/* ═══ HEADER ═══ */}
      <div className="card radius-12 mb-20">
        <div className="card-body p-24">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                style={{
                  width: 64,
                  height: 64,
                  fontSize: 24,
                  fontWeight: 700,
                  background: "rgba(99,102,241,0.15)",
                  color: "#6366f1",
                }}
              >
                {estudiante.nombre_completo?.[0]?.toUpperCase() || "?"}
              </div>
              <div style={{ minWidth: 0 }}>
                <h4 className="mb-1 fw-bold text-primary-light">
                  {estudiante.nombre_completo}
                </h4>
                <div className="d-flex flex-wrap gap-3 small text-secondary-light">
                  <span>
                    <Icon icon="mdi:email-outline" className="me-1" />
                    {estudiante.email}
                  </span>
                  {estudiante.plantel_nombre && (
                    <span>
                      <Icon icon="mdi:office-building-outline" className="me-1" />
                      {estudiante.plantel_nombre}
                    </span>
                  )}
                  {estudiante.escuela_nombre && (
                    <span>
                      <Icon icon="mdi:school-outline" className="me-1" />
                      {estudiante.escuela_nombre}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <Link
              to="/mis-alumnos"
              className="btn btn-outline-secondary rounded-pill px-3"
            >
              <Icon icon="mdi:arrow-left" width="16" className="me-1" />
              Volver
            </Link>
          </div>
        </div>
      </div>

      {/* ═══ TALENTOS ═══ */}
      <div className="card radius-12 mb-20">
        <div className="card-body p-24">
          <SectionHeader
            icon="mdi:star-shooting"
            title="Talentos"
            subtitle="Los que más y menos dominan a partir del quiz inicial."
            accent="#6366f1"
          />
          <div className="row g-4">
            <div className="col-lg-6 col-12">
              <div className="d-flex align-items-center gap-2 mb-3">
                <Icon icon="mdi:star-check" width="18" style={{ color: "#10b981" }} />
                <h6 className="mb-0 fw-bold text-primary-light">Dominantes</h6>
                <span
                  className="badge rounded-pill ms-auto"
                  style={{ background: "rgba(16,185,129,0.15)", color: "#10b981" }}
                >
                  {talentos_optimos.length}
                </span>
              </div>
              <TalentoList items={talentos_optimos} accent="#10b981" />
            </div>
            <div className="col-lg-6 col-12">
              <div className="d-flex align-items-center gap-2 mb-3">
                <Icon icon="mdi:trending-down" width="18" style={{ color: "#f59e0b" }} />
                <h6 className="mb-0 fw-bold text-primary-light">No dominantes</h6>
                <span
                  className="badge rounded-pill ms-auto"
                  style={{ background: "rgba(245,158,11,0.15)", color: "#f59e0b" }}
                >
                  {talentos_a_mejorar.length}
                </span>
              </div>
              <TalentoList items={talentos_a_mejorar} accent="#f59e0b" />
            </div>
          </div>
        </div>
      </div>

      {/* ═══ TABS (estilo estándar de la app: bordered-tab nav-pills) ═══ */}
      <div className="card radius-12 mb-20">
        {/* HEADER — solo navegación */}
        <div className="card-header border-bottom bg-base ps-0 py-0">
          <ul className="nav bordered-tab nav-pills mb-0 flex-wrap">
            {TABS.map((t) => {
              const isActive = activeTab === t.id;
              const count = tabCounts[t.id] || 0;
              return (
                <li className="nav-item" key={t.id}>
                  <button
                    type="button"
                    onClick={() => setActiveTab(t.id)}
                    className={`nav-link d-inline-flex align-items-center gap-2 ${isActive ? "active" : ""}`}
                  >
                    <Icon icon={t.icon} width="18" />
                    <span>{t.label}</span>
                    {count > 0 && (
                      <span
                        className="badge rounded-pill ms-1 bg-neutral-100 text-secondary-light"
                        style={{ fontSize: "0.65rem", padding: "3px 7px" }}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* BODY — contenido del tab activo */}
        <div className="card-body">
          {activeTab === "resumen" && (() => {
            if (mis_fortalezas.length === 0) {
              return (
                <EmptyTab
                  icon="mdi:shield-off-outline"
                  text="El alumno aún no ha terminado esta parte."
                />
              );
            }
            const talentosUnicos = new Set();
            mis_fortalezas.forEach((a) =>
              a.talentos.forEach((t) => talentosUnicos.add(t.talento)),
            );
            const totalTalentos = talentosUnicos.size;
            const totalAreas = mis_fortalezas.length;
            return (
              <>
                {/* Banner resumen */}
                <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded-3 bg-neutral-50">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{
                      width: 48,
                      height: 48,
                      background: "linear-gradient(135deg, #10b981, #059669)",
                      color: "#fff",
                    }}
                  >
                    <Icon icon="mdi:shield-star" width="26" />
                  </div>
                  <div>
                    <h6 className="mb-1 fw-bold text-primary-light">
                      Sus fortalezas
                    </h6>
                    <p className="text-secondary-light mb-0 small">
                      {totalTalentos} talento{totalTalentos !== 1 ? "s" : ""}{" "}
                      descubierto{totalTalentos !== 1 ? "s" : ""} en {totalAreas}{" "}
                      área{totalAreas !== 1 ? "s" : ""}
                    </p>
                  </div>
                </div>

                <div className="row">
                  {mis_fortalezas.map((item, idx) => (
                    <AreaCard key={idx} item={item} cardIndex={idx} />
                  ))}
                </div>
              </>
            );
          })()}

          {activeTab === "confirmacion" && (
            confirmacion.length === 0 ? (
              <EmptyTab
                icon="mdi:check-decagram-outline"
                text="El alumno aún no ha respondido el cuestionario de confirmación."
              />
            ) : (
              <>
                <GroupToggle
                  value={confirmacionGroup}
                  onChange={setConfirmacionGroup}
                  options={[
                    { id: "pregunta", label: "Por pregunta", icon: "mdi:comment-question-outline" },
                    { id: "talento", label: "Por talento", icon: "mdi:star-outline" },
                  ]}
                />
                <div className="row">
                  {confirmacionGroup === "pregunta"
                    ? confirmacion.map((item, idx) => (
                        <AreaCard key={idx} item={item} cardIndex={idx} />
                      ))
                    : agruparPorTalento(confirmacion).map((item, idx) => (
                        <TalentoCard
                          key={idx}
                          item={item}
                          cardIndex={idx}
                          subLabel="pregunta"
                        />
                      ))}
                </div>
              </>
            )
          )}

          {activeTab === "fortalezas" && (
            fortalezas.length === 0 ? (
              <EmptyTab
                icon="mdi:shield-star-outline"
                text="El alumno aún no ha respondido el cuestionario de fortalezas."
              />
            ) : (
              <>
                <GroupToggle
                  value={fortalezasGroup}
                  onChange={setFortalezasGroup}
                  options={[
                    { id: "area", label: "Por área", icon: "mdi:view-grid-outline" },
                    { id: "talento", label: "Por talento", icon: "mdi:star-outline" },
                  ]}
                />
                <div className="row">
                  {fortalezasGroup === "area"
                    ? fortalezas.map((item, idx) => (
                        <AreaCard key={idx} item={item} cardIndex={idx} />
                      ))
                    : agruparPorTalento(fortalezas).map((item, idx) => (
                        <TalentoCard
                          key={idx}
                          item={item}
                          cardIndex={idx}
                          subLabel="área"
                        />
                      ))}
                </div>
              </>
            )
          )}

          {activeTab === "aplicacion" && (
            aplicacion.length === 0 ? (
              <EmptyTab
                icon="mdi:briefcase-check-outline"
                text="El alumno aún no ha respondido el cuestionario de aplicación."
              />
            ) : (
              <div className="row">
                {aplicacion.map((grupo, idx) => (
                  <AplicacionCard key={idx} grupo={grupo} />
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

const EmptyTab = ({ icon, text }) => (
  <div className="text-center py-5 rounded-3 bg-neutral-50">
    <Icon icon={icon} width="38" className="text-secondary-light" />
    <p className="text-secondary-light mb-0 mt-2">{text}</p>
  </div>
);

export default AlumnoDetallePage;
